import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../utils/translationDictionaries';
import { PUERTO_RICO_CITIES } from '../utils/constants';
import './SearchBar.css';

const SearchBar = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslations(language);
  const [searchParams, setSearchParams] = useState({
    city: '',
    bathrooms: '',
    bedrooms: '',
    listingType: 'for-sale',
    minPrice: '',
    maxPrice: ''
  });

  const handleChange = (e) => {
    setSearchParams({
      ...searchParams,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Build query parameters
    const params = new URLSearchParams();
    
    if (searchParams.city) params.append('city', searchParams.city);
    if (searchParams.bathrooms) params.append('bathrooms', searchParams.bathrooms);
    if (searchParams.bedrooms) params.append('bedrooms', searchParams.bedrooms);
    if (searchParams.minPrice) params.append('minPrice', searchParams.minPrice);
    if (searchParams.maxPrice) params.append('maxPrice', searchParams.maxPrice);
    
    // Navigate to the appropriate listing page
    const basePath = `/properties/${searchParams.listingType}`;
    const queryString = params.toString();
    navigate(queryString ? `${basePath}?${queryString}` : basePath);
  };

  // Price range options - using translations
  const priceRanges = [
    { label: t('search.allPrices'), min: '', max: '' },
    { label: '$0 - $50,000', min: '0', max: '50000' },
    { label: '$50,000 - $100,000', min: '50000', max: '100000' },
    { label: '$100,000 - $150,000', min: '100000', max: '150000' },
    { label: '$150,000 - $200,000', min: '150000', max: '200000' },
    { label: '$200,000 - $300,000', min: '200000', max: '300000' },
    { label: '$300,000+', min: '300000', max: '' }
  ];

  const handlePriceRangeChange = (e) => {
    const selectedRange = priceRanges.find(range => range.label === e.target.value);
    if (selectedRange) {
      setSearchParams({
        ...searchParams,
        minPrice: selectedRange.min,
        maxPrice: selectedRange.max
      });
    }
  };

  return (
    <div className="search-bar">
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-form-grid">
          <select 
            name="city" 
            value={searchParams.city} 
            onChange={handleChange}
            className="search-input"
          >
            <option value="">{t('search.city')}</option>
            {PUERTO_RICO_CITIES.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <select 
            name="bathrooms" 
            value={searchParams.bathrooms} 
            onChange={handleChange}
            className="search-input"
          >
            <option value="">{t('search.bathrooms')}</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>

          <select 
            name="bedrooms" 
            value={searchParams.bedrooms} 
            onChange={handleChange}
            className="search-input"
          >
            <option value="">{t('search.bedrooms')}</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
            <option value="5">5+</option>
          </select>

          <select 
            name="listingType" 
            value={searchParams.listingType} 
            onChange={handleChange}
            className="search-input"
          >
            <option value="for-sale">{t('search.forSale')}</option>
            <option value="for-rent">{t('search.forRent')}</option>
            <option value="sold">{t('navbar.sold')}</option>
          </select>

          <select 
            onChange={handlePriceRangeChange}
            className="search-input"
            value={priceRanges.find(r => r.min === searchParams.minPrice && r.max === searchParams.maxPrice)?.label || ''}
          >
            {priceRanges.map((range) => (
              <option key={range.label} value={range.label}>{range.label}</option>
            ))}
          </select>

          <button type="submit" className="search-button">
            {t('search.search')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
